import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Dropzone from 'react-dropzone';
import { Button } from '@this/shared/ui/inputs/button';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import { Fetcher } from '@this/src/util';

interface Timetable {
  flight_number: string;
  departure_airport_code: string;
  arrival_airport_code: string;
  departure_time: string;
  arrival_time: string;
  operating_carrier: string | null;
  raw_remarks: string | null;
}

interface TimetableListResponse {
  data: Timetable[];
  total: number;
  page: number;
  per_page: number;
}

const PER_PAGE = 50;

const TimetableImportTable: React.FC = () => {
  const styles = useMemo(() => getStyles(), []);

  const [csvText, setCsvText] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timetables, setTimetables] = useState<Timetable[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [listLoading, setListLoading] = useState<boolean>(false);

  const totalPages = Math.ceil(total / PER_PAGE);

  const fetchTimetables = useCallback(async (targetPage: number) => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', targetPage.toString());
      const res = await Fetcher.get<TimetableListResponse>(`/god/domestic_air/timetables?${params.toString()}`);
      setTimetables(res.data);
      setTotal(res.total);
    } catch (e) {
      setError('時刻表一覧の取得に失敗しました。');
    }
    setListLoading(false);
  }, []);

  useEffect(() => {
    fetchTimetables(page);
  }, [page, fetchTimetables]);

  const handleDrop = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsText(file, 'UTF-8');
  }, []);

  const handleImport = async () => {
    if (!csvText) {
      setError('CSVファイルを選択してください。');
      return;
    }

    setLoading(true);
    setMessage(null);
    setError(null);
    try {
      const res = await Fetcher.post<{ message: string }>('/god/domestic_air/import_timetable', {
        csv: csvText
      });
      setMessage(res.message);
      setPage(1);
      await fetchTimetables(1);
    } catch (e) {
      setError('時刻表の取り込み中にエラーが発生しました。');
    }
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>ANA国内線時刻表の取り込み</h2>
      {message && (
        <Alert variant="success" onClose={() => setMessage(null)} style={styles.alert}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} style={styles.alert}>
          {error}
        </Alert>
      )}
      <p style={styles.description}>便明細CSVを選択してください。取り込むと既存データは全件洗い替えされます。</p>
      <div style={styles.dropzoneWrap}>
        <Dropzone onDrop={handleDrop} accept=".csv" style={styles.dropzone}>
          <p style={styles.dropzoneText}>
            ここへ時刻表CSVファイルをドラッグ&amp;ドロップ
            <br />
            または
            <br />
            クリックしてCSVファイルを指定
          </p>
        </Dropzone>
        {fileName && <div style={styles.fileName}>選択中: {fileName}</div>}
      </div>
      <div style={styles.importButtonRow}>
        <Button onClick={handleImport} disabled={loading} loading={loading}>
          取り込む
        </Button>
      </div>

      <h2 style={styles.listTitle}>取り込み済み時刻表</h2>
      <div style={styles.listCount}>
        {total === 0
          ? '0件'
          : `全${total}件中 ${(page - 1) * PER_PAGE + 1}〜${Math.min(page * PER_PAGE, total)}件表示`}
      </div>
      {listLoading ? (
        <Loading />
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>便名</th>
              <th style={styles.th}>出発空港</th>
              <th style={styles.th}>到着空港</th>
              <th style={styles.th}>出発時刻</th>
              <th style={styles.th}>到着時刻</th>
              <th style={styles.th}>運航社</th>
              <th style={styles.th}>備考</th>
            </tr>
          </thead>
          <tbody>
            {timetables.map((t, index) => (
              <tr key={`${t.flight_number}_${t.departure_airport_code}_${t.arrival_airport_code}_${index}`}>
                <td style={styles.td}>{t.flight_number}</td>
                <td style={styles.td}>{t.departure_airport_code}</td>
                <td style={styles.td}>{t.arrival_airport_code}</td>
                <td style={styles.td}>{t.departure_time}</td>
                <td style={styles.td}>{t.arrival_time}</td>
                <td style={styles.td}>{t.operating_carrier}</td>
                <td style={styles.td}>{t.raw_remarks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={styles.pagination}>
        <Button onClick={() => setPage(1)} disabled={page === 1}>
          {'<<'}
        </Button>
        <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
          {'<'}
        </Button>
        <span style={styles.pageInfo}>
          {page} / {Math.max(totalPages, 1)}
        </span>
        <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
          {'>'}
        </Button>
        <Button onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
          {'>>'}
        </Button>
      </div>
    </div>
  );
};

const getStyles = (): { [key: string]: React.CSSProperties } => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    margin: '20px',
    maxWidth: '960px'
  },
  title: {
    marginBottom: '20px'
  },
  alert: {
    marginBottom: '16px'
  },
  description: {
    marginBottom: '8px',
    fontSize: '13px',
    color: '#555'
  },
  dropzoneWrap: {
    marginBottom: '16px'
  },
  dropzone: {
    width: '480px',
    background: '#eee',
    border: '2px dotted #999',
    textAlign: 'center',
    padding: '20px',
    cursor: 'pointer'
  },
  dropzoneText: {
    margin: 0
  },
  fileName: {
    marginTop: '8px',
    fontSize: '13px'
  },
  importButtonRow: {
    marginBottom: '32px'
  },
  listTitle: {
    marginBottom: '12px'
  },
  listCount: {
    marginBottom: '8px',
    fontSize: '13px'
  },
  table: {
    fontSize: '12px',
    borderCollapse: 'collapse',
    width: '100%'
  },
  th: {
    textAlign: 'left',
    borderBottom: '1px solid #ccc',
    padding: '6px 8px',
    whiteSpace: 'nowrap'
  },
  td: {
    borderBottom: '1px solid #eee',
    padding: '6px 8px',
    whiteSpace: 'nowrap'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '20px 0',
    gap: '8px'
  },
  pageInfo: {
    fontSize: '12px'
  }
});

export default TimetableImportTable;
